import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import { useSocket } from '../../../../hooks/useSocket';
import { useReactToPrint } from 'react-to-print';
import { Shield, Users, Clock, CheckCircle2, Play, Square, FileText, UserCheck, AlertTriangle, ChevronDown, ChevronRight, Printer } from 'lucide-react';
import EmergencyReportPrint from '../../../../components/EmergencyReportPrint';

const EmergencyRoster = () => {
  const { currentUser } = useAuth();
  const { socket, isConnected } = useSocket(currentUser?.id);
  
  // State management
  const [activeSession, setActiveSession] = useState(null);
  const [unaccountedEmployees, setUnaccountedEmployees] = useState([]);
  const [accountedEmployees, setAccountedEmployees] = useState([]);
  const [activeTab, setActiveTab] = useState('unaccounted');
  const [loading, setLoading] = useState(false);
  const [isStartingSession, setIsStartingSession] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [allSessions, setAllSessions] = useState([]);
  const [showReports, setShowReports] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const printRef = useRef();
  
  // Grouping functionality state
  const [showGroupingDropdown, setShowGroupingDropdown] = useState(false);
  const [groupingMode, setGroupingMode] = useState('alphabetical');
  const [groupedEmployees, setGroupedEmployees] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});

  // Get API URL
  const API_URL = process.env.REACT_APP_API_URL || 'https://ksp3plhq.com/api';

  // Check if user has manager/admin permissions
  const hasPermission = () => {
    const userRoles = currentUser?.roles || [];
    // userRoles is an array of objects with {id, name}, not just IDs
    return userRoles.some(role => role.id === 1 || role.id === 2); // Admin(1) or Manager(2)
  };

  // Load active emergency session
  const loadActiveSession = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/emergency-roster/active`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load active session');
      }

      const data = await response.json();
      setActiveSession(data.activeSession);
      setUnaccountedEmployees(data.unaccountedEmployees || []);
      setAccountedEmployees(data.accountedEmployees || []);
    } catch (err) {
      console.error('Error loading active session:', err);
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  // Load active session on component mount
  useEffect(() => {
    if (currentUser && hasPermission()) {
      loadActiveSession();
    }
  }, [currentUser, loadActiveSession]);

  // WebSocket event listeners
  useEffect(() => {
    if (!socket) return;

    const handleEmployeeAccounted = (data) => {
      console.log('🚨 WebSocket: employee-accounted', data);
      if (data.sessionId === activeSession?.id) {
        // Remove from unaccounted, add to accounted
        setUnaccountedEmployees(prev => 
          prev.filter(emp => emp.employee_id !== data.accountedEmployee.employee_id)
        );
        setAccountedEmployees(prev => [...prev, data.accountedEmployee]);
        
        // Show completion modal if all accounted
        if (data.isComplete) {
          setShowCompleteModal(true);
          setTimeout(() => setShowCompleteModal(false), 5000);
        }
      }
    };

    const handleEmployeeUnaccounted = (data) => {
      console.log('🚨 WebSocket: employee-unaccounted', data);
      if (data.sessionId === activeSession?.id) {
        // Remove from accounted, add back to unaccounted
        setAccountedEmployees(prev => 
          prev.filter(emp => emp.employee_id !== data.employeeId)
        );
        // Reload session to get updated unaccounted list
        loadActiveSession();
      }
    };

    const handleSessionComplete = (data) => {
      console.log('🚨 WebSocket: session-complete', data);
      if (data.sessionId === activeSession?.id) {
        setShowCompleteModal(true);
        setTimeout(() => setShowCompleteModal(false), 5000);
      }
    };

    const handleSessionEnded = (data) => {
      console.log('🚨 WebSocket: emergency-session-ended', data);
      setActiveSession(null);
      setUnaccountedEmployees([]);
      setAccountedEmployees([]);
    };

    const handleSessionStarted = (data) => {
      console.log('🚨 WebSocket: emergency-session-started', data);
      // Always reload active session when a new session starts
      loadActiveSession();
    };

    socket.on('employee-accounted', handleEmployeeAccounted);
    socket.on('employee-unaccounted', handleEmployeeUnaccounted);
    socket.on('session-complete', handleSessionComplete);
    socket.on('emergency-session-started', handleSessionStarted);
    socket.on('emergency-session-ended', handleSessionEnded);

    return () => {
      socket.off('employee-accounted', handleEmployeeAccounted);
      socket.off('employee-unaccounted', handleEmployeeUnaccounted);
      socket.off('session-complete', handleSessionComplete);
      socket.off('emergency-session-started', handleSessionStarted);
      socket.off('emergency-session-ended', handleSessionEnded);
    };
  }, [socket, activeSession]);

  // Start emergency session
  const startEmergencySession = async (type = 'drill') => {
    try {
      setIsStartingSession(true);

      const name = type === 'drill' 
        ? `Drill - ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`
        : `Emergency - ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`;

      const response = await fetch(`${API_URL}/emergency-roster/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ name, type })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to start emergency session');
      }

      const data = await response.json();
      await loadActiveSession();
    } catch (err) {
      console.error('Error starting session:', err);
    } finally {
      setIsStartingSession(false);
    }
  };

  // End emergency session
  const endEmergencySession = async () => {
    if (!activeSession) return;

    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/emergency-roster/sessions/${activeSession.id}/end`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to end emergency session');
      }

      // WebSocket will handle the UI updates
      setActiveSession(null);
      setUnaccountedEmployees([]);
      setAccountedEmployees([]);
    } catch (err) {
      console.error('Error ending session:', err);
    } finally {
      setLoading(false);
    }
  };

  // Account for employee
  const accountForEmployee = async (employeeId) => {
    if (!activeSession) return;

    try {
      const response = await fetch(`${API_URL}/emergency-roster/account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          sessionId: activeSession.id,
          employeeId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to account for employee');
      }

      // Success handled by WebSocket event
    } catch (err) {
      console.error('Error accounting for employee:', err);
    }
  };

  // Unaccount employee
  const unaccountEmployee = async (rosterId) => {
    try {
      const response = await fetch(`${API_URL}/emergency-roster/account/${rosterId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to unaccount employee');
      }

      // Success handled by WebSocket event
    } catch (err) {
      console.error('Error unaccounting employee:', err);
    }
  };

  // Load all sessions for reports
  const loadAllSessions = async () => {
    try {
      const response = await fetch(`${API_URL}/emergency-roster/sessions`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load sessions');
      }

      const data = await response.json();
      setAllSessions(data.sessions || []);
    } catch (err) {
      console.error('Error loading sessions:', err);
    }
  };

  // Load specific session report
  const loadSessionReport = async (sessionId) => {
    try {
      const response = await fetch(`${API_URL}/emergency-roster/sessions/${sessionId}/report`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load session report');
      }

      const data = await response.json();
      setSelectedReport(data);
    } catch (err) {
      console.error('Error loading session report:', err);
    }
  };

  // Print functionality
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Emergency_Report_${selectedReport?.session?.name?.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}`,
    pageStyle: `
      @page {
        size: A4;
        margin: 0.5in;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
        }
      }
    `,
  });

  // Format time
  const formatTime = (timeString) => {
    return new Date(timeString).toLocaleTimeString();
  };

  // Enhanced tab click handler for unaccounted tab
  const handleUnaccountedTabClick = () => {
    if (activeTab === 'unaccounted') {
      // Already on unaccounted tab - toggle dropdown
      setShowGroupingDropdown(!showGroupingDropdown);
    } else {
      // Switch to unaccounted tab
      setActiveTab('unaccounted');
      setShowGroupingDropdown(false);
    }
  };

  // Group employees by specified mode
  const groupEmployees = (employees, mode) => {
    if (mode === 'alphabetical') {
      return { 'All Employees': employees.sort((a, b) => a.full_name.localeCompare(b.full_name)) };
    }

    const grouped = employees.reduce((groups, employee) => {
      let key;
      switch (mode) {
        case 'shift':
          key = employee.shift || 'No Shift';
          break;
        case 'team':
          key = employee.team || 'No Team';
          break;
        case 'work_group':
          key = employee.work_group || 'No Group';
          break;
        case 'activity':
          key = employee.activity || 'No Activity';
          break;
        case 'business_unit':
          key = employee.business_unit || 'No Business Unit';
          break;
        default:
          key = 'All Employees';
      }
      
      if (!groups[key]) groups[key] = [];
      groups[key].push(employee);
      return groups;
    }, {});

    // Sort employees within each group alphabetically
    Object.keys(grouped).forEach(key => {
      grouped[key].sort((a, b) => a.full_name.localeCompare(b.full_name));
    });

    return grouped;
  };

  // Toggle group expansion
  const toggleGroupExpansion = (groupName) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  // Update grouped employees when grouping mode or unaccounted employees change
  useEffect(() => {
    const grouped = groupEmployees(unaccountedEmployees, groupingMode);
    setGroupedEmployees(grouped);
  }, [unaccountedEmployees, groupingMode]);

  // Reset expanded groups only when grouping mode changes (not when employee data changes)
  useEffect(() => {
    if (groupingMode !== 'alphabetical') {
      setExpandedGroups({});
    }
  }, [groupingMode]);

  // Check permissions
  if (!currentUser || !hasPermission()) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800 border border-red-700 rounded-lg shadow-sm p-6 text-center max-w-md w-full">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-medium text-slate-200 mb-2">Access Denied</h2>
          <p className="text-slate-400">Only Managers and Administrators can access the Emergency Roster.</p>
        </div>
      </div>
    );
  }

  // Reports view
  if (showReports) {
    return (
      <div className="min-h-screen bg-slate-900 p-3 sm:p-4">
        <div className="max-w-6xl mx-auto">
          <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-slate-200 mb-1">Emergency Session Reports</h1>
              <p className="text-slate-400">View past emergency and drill sessions</p>
            </div>
            <button
              onClick={() => {setShowReports(false); setSelectedReport(null);}}
              className="px-4 py-2 bg-slate-700 text-slate-200 rounded-lg hover:bg-slate-600 border border-slate-600 text-sm sm:text-base"
            >
              Back to Roster
            </button>
          </div>

          {selectedReport ? (
            <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm p-4 sm:p-6">
              <div className="mb-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-3">
                  <h2 className="text-lg sm:text-xl font-semibold text-slate-200">{selectedReport.session.name}</h2>
                  <div className="flex gap-3 self-start sm:self-auto">
                    <button
                      onClick={handlePrint}
                      className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                    >
                      <Printer className="w-4 h-4 mr-2" />
                      Print PDF
                    </button>
                    <button
                      onClick={() => setSelectedReport(null)}
                      className="text-slate-400 hover:text-slate-200 text-sm"
                    >
                      ← Back to Sessions
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
                  <div className="bg-slate-700 border border-slate-600 p-3 rounded-lg">
                    <div className="text-xs sm:text-sm text-slate-400">Total Employees</div>
                    <div className="text-lg sm:text-xl font-semibold text-slate-200">{selectedReport.summary.totalEmployees}</div>
                  </div>
                  <div className="bg-green-900/30 border border-green-700 p-3 rounded-lg">
                    <div className="text-xs sm:text-sm text-slate-400">Accounted For</div>
                    <div className="text-lg sm:text-xl font-semibold text-green-400">{selectedReport.summary.accountedEmployees}</div>
                  </div>
                  <div className="bg-blue-900/30 border border-blue-700 p-3 rounded-lg">
                    <div className="text-xs sm:text-sm text-slate-400">Completion Rate</div>
                    <div className="text-lg sm:text-xl font-semibold text-blue-400">{selectedReport.summary.completionRate}%</div>
                  </div>
                  <div className="bg-yellow-900/30 border border-yellow-700 p-3 rounded-lg">
                    <div className="text-xs sm:text-sm text-slate-400">Duration</div>
                    <div className="text-lg sm:text-xl font-semibold text-yellow-400">{selectedReport.summary.durationMinutes || 0} min</div>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <h3 className="text-base sm:text-lg font-medium mb-3 text-slate-200">Accounted Employees</h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {selectedReport.rosterEntries.map(entry => (
                      <div key={entry.id} className="flex justify-between items-center p-3 bg-green-900/20 border border-green-700 rounded-lg">
                        <span className="font-medium text-slate-200 text-sm sm:text-base">{entry.employee_name}</span>
                        <div className="text-xs sm:text-sm text-slate-400 text-right">
                          <div>{formatTime(entry.accounted_at)}</div>
                          <div>by {entry.accounted_by_name}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-base sm:text-lg font-medium mb-3 text-slate-200">Managers Participated</h3>
                  <div className="space-y-2">
                    {selectedReport.managers.map(manager => (
                      <div key={manager.id} className="flex justify-between items-center p-3 bg-blue-900/20 border border-blue-700 rounded-lg">
                        <span className="font-medium text-slate-200 text-sm sm:text-base">{manager.manager_name}</span>
                        <span className="text-xs sm:text-sm text-slate-400">{manager.employees_accounted} employees</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Hidden print component */}
              <div style={{ display: 'none' }}>
                <EmergencyReportPrint ref={printRef} reportData={selectedReport} />
              </div>
            </div>
          ) : (
            <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm">
              <div className="p-4 sm:p-6">
                <button
                  onClick={loadAllSessions}
                  className="mb-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm sm:text-base"
                >
                  Load Sessions
                </button>
                
                <div className="space-y-2">
                  {allSessions.map(session => (
                    <div 
                      key={session.id}
                      className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border border-slate-700 rounded-lg hover:bg-slate-700/50 cursor-pointer gap-3"
                      onClick={() => loadSessionReport(session.id)}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-slate-200 text-sm sm:text-base truncate">{session.name}</div>
                        <div className="text-xs sm:text-sm text-slate-400">
                          Started by {session.started_by_name} • {new Date(session.started_at).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-xs sm:text-sm font-medium text-slate-300">
                          {session.accounted_employees}/{session.total_employees} accounted
                        </div>
                        <div className="text-xs text-slate-500">
                          {session.duration_minutes} minutes
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Success Modal for Complete Session */}
      {showCompleteModal && (
        <div className="fixed inset-0 bg-green-600 bg-opacity-95 flex items-center justify-center z-50 p-4">
          <div className="text-center text-white p-6 sm:p-8 max-w-sm sm:max-w-md mx-auto">
            <CheckCircle2 className="w-16 sm:w-24 h-16 sm:h-24 mx-auto mb-4" />
            <h2 className="text-2xl sm:text-4xl font-bold mb-2">All Clear!</h2>
            <p className="text-lg sm:text-xl">Everyone has been accounted for</p>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto p-2 sm:p-4">
        {/* Header */}
        <div className="mb-3 sm:mb-6">
          <div className="flex items-center justify-between mb-3 sm:mb-4 gap-2 sm:gap-4">
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <h1 className="text-lg sm:text-2xl font-bold text-slate-200 mb-0 sm:mb-1">Emergency Roster</h1>
                <button
                  onClick={() => setShowReports(true)}
                  className="flex items-center justify-center px-2 sm:px-4 py-1 sm:py-2 bg-slate-700 text-slate-200 rounded-lg hover:bg-slate-600 border border-slate-600 text-xs sm:text-base flex-shrink-0"
                >
                  <FileText className="w-3 sm:w-4 h-3 sm:h-4 mr-1 sm:mr-2" />
                  Reports
                </button>
              </div>
              <p className="text-slate-400 text-sm sm:text-base hidden sm:block">Account for all personnel during emergencies</p>
            </div>
          </div>

          {/* Connection Status */}
          <div className={`px-2 sm:px-3 py-1 rounded-md inline-flex items-center text-xs sm:text-sm mb-3 sm:mb-4 ${isConnected ? 'bg-green-900/30 text-green-300' : 'bg-red-900/30 text-red-300'}`}>
            <span className={`w-1.5 sm:w-2 h-1.5 sm:h-2 rounded-full mr-1 sm:mr-2 ${isConnected ? 'bg-green-400' : 'bg-red-400'}`}></span>
            {isConnected ? 'Connected' : 'Disconnected'} 
          </div>

          {/* Session Controls */}
          {!activeSession ? (
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-3 sm:mb-6">
              <button
                onClick={() => startEmergencySession('drill')}
                disabled={isStartingSession}
                className="flex items-center justify-center px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-xs sm:text-base min-h-[40px] sm:min-h-[48px]"
              >
                <Play className="w-3 sm:w-5 h-3 sm:h-5 mr-1 sm:mr-2" />
                {isStartingSession ? 'Starting...' : 'Start Drill'}
              </button>
              <button
                onClick={() => startEmergencySession('emergency')}
                disabled={isStartingSession}
                className="flex items-center justify-center px-4 sm:px-6 py-2 sm:py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-xs sm:text-base min-h-[40px] sm:min-h-[48px]"
              >
                <AlertTriangle className="w-3 sm:w-5 h-3 sm:h-5 mr-1 sm:mr-2" />
                {isStartingSession ? 'Starting...' : 'Start Emergency'}
              </button>
            </div>
          ) : (
            <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm p-3 sm:p-4 mb-3 sm:mb-6">
              <div className="flex items-center justify-between mb-2 sm:mb-4 gap-2">
                <div className="min-w-0 flex-1">
                  <h2 className="text-sm sm:text-lg font-semibold text-slate-200 truncate">{activeSession.name}</h2>
                  <p className="text-xs text-slate-400">
                    <span className="hidden sm:inline">Started at {formatTime(activeSession.started_at)} by {activeSession.started_by_name}</span>
                    <span className="sm:hidden">{formatTime(activeSession.started_at)}</span>
                  </p>
                </div>
                <button
                  onClick={endEmergencySession}
                  disabled={loading}
                  className="flex items-center justify-center px-2 sm:px-4 py-1 sm:py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-xs sm:text-sm min-h-[28px] sm:min-h-[40px] flex-shrink-0"
                >
                  <Square className="w-3 sm:w-4 h-3 sm:h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">End Session</span>
                  <span className="sm:hidden">End</span>
                </button>
              </div>
              
              <div className="grid grid-cols-3 gap-2 sm:gap-4">
                <div className="bg-slate-700 border border-slate-600 p-2 sm:p-3 rounded-lg">
                  <div className="flex items-center">
                    <Users className="w-3 sm:w-5 h-3 sm:h-5 text-slate-400 mr-1 sm:mr-2" />
                    <div>
                      <div className="text-xs text-slate-400">Total</div>
                      <div className="text-sm sm:text-xl font-semibold text-slate-200">{unaccountedEmployees.length + accountedEmployees.length}</div>
                    </div>
                  </div>
                </div>
                <div className="bg-green-900/30 border border-green-700 p-2 sm:p-3 rounded-lg">
                  <div className="flex items-center">
                    <CheckCircle2 className="w-3 sm:w-5 h-3 sm:h-5 text-green-400 mr-1 sm:mr-2" />
                    <div>
                      <div className="text-xs text-slate-400">Safe</div>
                      <div className="text-sm sm:text-xl font-semibold text-green-400">{accountedEmployees.length}</div>
                    </div>
                  </div>
                </div>
                <div className="bg-yellow-900/30 border border-yellow-700 p-2 sm:p-3 rounded-lg">
                  <div className="flex items-center">
                    <Clock className="w-3 sm:w-5 h-3 sm:h-5 text-yellow-400 mr-1 sm:mr-2" />
                    <div>
                      <div className="text-xs text-slate-400">Missing</div>
                      <div className="text-sm sm:text-xl font-semibold text-yellow-400">{unaccountedEmployees.length}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>


        {/* Main Content */}
        {activeSession && (
          <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm">
            {/* Tabs */}
            <div className="border-b border-slate-700">
              <div className="flex">
                <button
                  onClick={handleUnaccountedTabClick}
                  className={`flex-1 px-4 sm:px-6 py-3 sm:py-4 text-center font-medium text-sm sm:text-base relative ${
                    activeTab === 'unaccounted'
                      ? 'border-b-2 border-red-500 text-red-400 bg-red-900/20'
                      : 'text-slate-400 hover:text-slate-200'
                  }`}
                >
                  <span className="flex items-center justify-center flex-wrap">
                    <AlertTriangle className="w-4 sm:w-5 h-4 sm:h-5 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">Unaccounted </span>
                    <span className="sm:hidden">Missing </span>
                    ({unaccountedEmployees.length})
                    {activeTab === 'unaccounted' && (
                      <ChevronDown className={`w-3 sm:w-4 h-3 sm:h-4 ml-1 transition-transform ${showGroupingDropdown ? 'rotate-180' : ''}`} />
                    )}
                  </span>
                </button>
                <button
                  onClick={() => {
                    setActiveTab('accounted');
                    setShowGroupingDropdown(false);
                  }}
                  className={`flex-1 px-4 sm:px-6 py-3 sm:py-4 text-center font-medium text-sm sm:text-base ${
                    activeTab === 'accounted'
                      ? 'border-b-2 border-green-500 text-green-400 bg-green-900/20'
                      : 'text-slate-400 hover:text-slate-200'
                  }`}
                >
                  <span className="flex items-center justify-center flex-wrap">
                    <CheckCircle2 className="w-4 sm:w-5 h-4 sm:h-5 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">Accounted </span>
                    <span className="sm:hidden">Safe </span>
                    ({accountedEmployees.length})
                  </span>
                </button>
              </div>
            </div>

            {/* Grouping Dropdown */}
            {showGroupingDropdown && activeTab === 'unaccounted' && (
              <div className="border-b border-slate-700 bg-slate-750 p-3">
                <div className="flex flex-wrap gap-2 items-center">
                  <span className="text-xs text-slate-400 mr-2">Group by:</span>
                  {[
                    { key: 'alphabetical', label: 'Alphabetical' },
                    { key: 'shift', label: 'Shift' },
                    { key: 'team', label: 'Team' },
                    { key: 'work_group', label: 'Group' },
                    { key: 'activity', label: 'Activity' },
                    { key: 'business_unit', label: 'Business Unit' }
                  ].map(option => (
                    <button
                      key={option.key}
                      onClick={() => {
                        setGroupingMode(option.key);
                        setShowGroupingDropdown(false);
                      }}
                      className={`px-2 sm:px-3 py-1 rounded text-xs sm:text-sm transition-colors ${
                        groupingMode === option.key
                          ? 'bg-red-600 text-white'
                          : 'bg-slate-600 text-slate-300 hover:bg-slate-500'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Tab Content */}
            <div className="p-3 sm:p-6">
              {activeTab === 'unaccounted' && (
                <div className="space-y-2 sm:space-y-3">
                  {unaccountedEmployees.length === 0 ? (
                    <div className="text-center py-6 sm:py-12 text-slate-400">
                      <CheckCircle2 className="w-8 sm:w-16 h-8 sm:h-16 mx-auto mb-2 sm:mb-4 text-green-400" />
                      <h3 className="text-sm sm:text-lg font-medium mb-1 sm:mb-2 text-slate-300">All employees accounted for!</h3>
                      <p className="text-xs sm:text-base">Everyone has been safely accounted for.</p>
                    </div>
                  ) : (
                    Object.entries(groupedEmployees).map(([groupName, employees]) => {
                      const isExpanded = groupingMode === 'alphabetical' || expandedGroups[groupName];
                      const hasMultipleGroups = Object.keys(groupedEmployees).length > 1;
                      
                      return (
                        <div key={groupName} className="space-y-2">
                          {hasMultipleGroups && (
                            <button
                              onClick={() => toggleGroupExpansion(groupName)}
                              className="w-full bg-slate-700 border border-slate-600 rounded-lg p-2 sm:p-3 hover:bg-slate-600 transition-colors"
                            >
                              <h3 className="font-medium text-slate-200 text-xs sm:text-base flex items-center justify-between">
                                <div className="flex items-center">
                                  <Users className="w-3 sm:w-4 h-3 sm:h-4 mr-1 sm:mr-2 flex-shrink-0" />
                                  {groupName} ({employees.length})
                                </div>
                                <div className="flex items-center">
                                  {isExpanded ? (
                                    <ChevronDown className="w-4 h-4 text-slate-400" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4 text-slate-400" />
                                  )}
                                </div>
                              </h3>
                            </button>
                          )}
                          {isExpanded && (
                            <div className="space-y-2">
                              {employees.map((employee) => (
                                <div
                                  key={employee.employee_id}
                                  className="flex items-center justify-between p-2 sm:p-4 border border-red-700 rounded-lg bg-red-900/20 gap-2 sm:gap-3"
                                >
                                  <div className="min-w-0 flex-1">
                                    <div className="font-medium text-slate-200 text-xs sm:text-base truncate">{employee.full_name}</div>
                                    <div className="text-xs text-slate-400 sm:mt-1">
                                      <span className="hidden sm:inline">
                                        Clocked in: {formatTime(employee.clock_in_time)} • Activity: {employee.activity || 'None'}
                                        {groupingMode !== 'business_unit' && employee.business_unit && ` • BU: ${employee.business_unit}`}
                                        {groupingMode !== 'shift' && employee.shift && ` • Shift: ${employee.shift}`}
                                      </span>
                                      <span className="sm:hidden">{formatTime(employee.clock_in_time)}</span>
                                    </div>
                                  </div>
                                  <button
                                    onClick={() => accountForEmployee(employee.employee_id)}
                                    className="flex items-center justify-center px-2 sm:px-4 py-2 sm:py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 text-xs sm:text-sm font-medium min-w-[80px] sm:min-w-[140px] min-h-[36px] sm:min-h-[48px] flex-shrink-0"
                                  >
                                    <UserCheck className="w-3 sm:w-4 h-3 sm:h-4 sm:mr-2" />
                                    <span className="hidden sm:inline ml-2">Account For</span>
                                    <span className="sm:hidden">✓</span>
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              )}

              {activeTab === 'accounted' && (
                <div className="space-y-3 sm:space-y-4">
                  {accountedEmployees.length === 0 ? (
                    <div className="text-center py-6 sm:py-12 text-slate-400">
                      <Users className="w-8 sm:w-16 h-8 sm:h-16 mx-auto mb-2 sm:mb-4" />
                      <h3 className="text-sm sm:text-lg font-medium mb-1 sm:mb-2 text-slate-300">No employees accounted yet</h3>
                      <p className="text-xs sm:text-base">Start accounting for employees to see them here.</p>
                    </div>
                  ) : (
                    // Group by accounted_by_name
                    Object.entries(
                      accountedEmployees.reduce((groups, employee) => {
                        const manager = employee.accounted_by_name;
                        if (!groups[manager]) groups[manager] = [];
                        groups[manager].push(employee);
                        return groups;
                      }, {})
                    ).map(([manager, employees]) => (
                      <div key={manager} className="bg-green-900/20 border border-green-700 rounded-lg p-2 sm:p-4">
                        <h3 className="font-medium text-green-400 mb-2 sm:mb-3 flex items-center text-xs sm:text-base">
                          <Shield className="w-3 sm:w-4 h-3 sm:h-4 mr-1 sm:mr-2 flex-shrink-0" />
                          <span className="truncate">By {manager} ({employees.length})</span>
                        </h3>
                        <div className="space-y-1 sm:space-y-2">
                          {employees.map((employee) => (
                            <div
                              key={employee.id}
                              className="flex items-center justify-between p-2 sm:p-3 bg-slate-800 border border-slate-600 rounded gap-2 sm:gap-3"
                            >
                              <div className="min-w-0 flex-1">
                                <div className="font-medium text-slate-200 text-xs sm:text-base truncate">{employee.employee_name}</div>
                                <div className="text-xs text-slate-400 sm:mt-1">
                                  <span className="hidden sm:inline">Accounted at: {formatTime(employee.accounted_at)}</span>
                                  <span className="sm:hidden">{formatTime(employee.accounted_at)}</span>
                                </div>
                              </div>
                              <button
                                onClick={() => unaccountEmployee(employee.id)}
                                className="text-red-400 hover:text-red-300 text-xs px-2 py-1 sm:px-3 sm:py-1 rounded border border-red-600 hover:bg-red-900/20 flex-shrink-0"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {!activeSession && (
          <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm p-6 sm:p-8 text-center">
            <Shield className="w-12 sm:w-16 h-12 sm:h-16 text-slate-500 mx-auto mb-4" />
            <h2 className="text-lg sm:text-xl font-medium text-slate-300 mb-2">No Active Emergency Session</h2>
            <p className="text-slate-400 mb-6 text-sm sm:text-base">Start a drill or emergency session to begin accounting for personnel.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmergencyRoster;